<template>
  <div class="auth-page">
    <div class="auth-brand">
      <span class="auth-brand__mark">27pub</span>
      <span class="auth-brand__sub">Poker Management</span>
    </div>

    <v-card class="auth-card" elevation="2">
      <div class="auth-card-header">
        <h2>Join the Beta</h2>
        <p>Free access. No credit card. Just your numbers.</p>
      </div>
      <v-card-text class="pa-6">
        <v-form @submit.prevent="userSignup" class="signup-form">
          <v-text-field
            label="Email"
            v-model="email"
            type="email"
            required
            variant="outlined"
            density="comfortable"
            class="mb-2"
            @keydown.enter.prevent="userSignup"
          ></v-text-field>
          <v-text-field
            label="Password"
            v-model="password"
            type="password"
            required
            variant="outlined"
            density="comfortable"
            class="mb-2"
            @keydown.enter.prevent="userSignup"
          ></v-text-field>
          <v-text-field
            label="Alias"
            v-model="alias"
            required
            variant="outlined"
            density="comfortable"
            hint="Letters and numbers only — this is your display name"
            persistent-hint
            class="mb-4"
            @keydown.enter.prevent="userSignup"
          ></v-text-field>

          <div class="access-section mb-4">
            <p class="access-label">Request access to:</p>
            <v-checkbox
              v-model="requestedPermissions.poker"
              label="Poker Session Tracker"
              color="primary"
              hide-details
              density="compact"
            ></v-checkbox>
            <v-checkbox
              v-model="requestedPermissions.tournament"
              label="Tournament Timer"
              color="primary"
              hide-details
              density="compact"
            ></v-checkbox>
          </div>

          <v-alert v-if="feedback" type="error" density="compact" class="mb-4">
            {{ feedback }}
          </v-alert>

          <v-btn color="primary" block size="large" @click="userSignup">
            Create Account
          </v-btn>
        </v-form>
      </v-card-text>
    </v-card>

    <div class="auth-links">
      <span class="text-medium-emphasis">Already have an account?</span>
      <router-link to="/login">Sign in</router-link>
    </div>

    <div class="auth-footer">
      <router-link to="/">← Back to home</router-link>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth'
import { useRouter } from 'vue-router'
import { db } from '@/firebaseConfig'
import { doc, setDoc } from 'firebase/firestore'
import { USERS_DB } from '@/constants/constants'
import { lookupUserByAlias } from '@/api/users'
import { useUiStore } from '@/stores/ui'

export default {
  name: 'UserSignup',
  setup() {
    const uiStore = useUiStore();
    onMounted(() => { uiStore.navBarVisible = false; });
    onUnmounted(() => { uiStore.navBarVisible = true; });

    const email = ref('');
    const password = ref('');
    const alias = ref('');
    const feedback = ref('');
    const router = useRouter();
    const requestedPermissions = reactive({
      poker: false,
      tournament: false
    });

    const userSignup = async () => {
      feedback.value = '';
      const trimmedAlias = alias.value.trim();
      const lowercaseAlias = trimmedAlias.toLowerCase();
      if (!trimmedAlias) {
        feedback.value = 'Please enter an alias.';
        return;
      }
      if (!/^[a-zA-Z0-9]+$/.test(trimmedAlias)) {
        feedback.value = 'Alias may only contain letters and numbers (no symbols or spaces).';
        return;
      }

      try {
        const existing = await lookupUserByAlias(lowercaseAlias);
        if (existing) {
          feedback.value = 'That alias is already taken. Please choose another.';
          return;
        }

        const cred = await createUserWithEmailAndPassword(getAuth(), email.value, password.value);
        const userRef = doc(db, USERS_DB, cred.user.uid);
        await setDoc(userRef, {
          alias: trimmedAlias,
          aliasLower: lowercaseAlias,
          user_id: cred.user.uid,
          permissions: {
            poker: requestedPermissions.poker ? 'pending' : 'none',
            tournament: requestedPermissions.tournament ? 'pending' : 'none',
            admin: false
          }
        });
        router.push({ name: 'HomePage' });
      } catch (err) {
        feedback.value = err.message;
      }
    };

    return {
      email,
      password,
      alias,
      feedback,
      userSignup,
      requestedPermissions
    };
  }
};
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  background: #F7F0E6;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  font-family: 'DM Sans', sans-serif;
}

.auth-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 28px;
}

.auth-brand__mark {
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: 2rem;
  color: #2D6A4F;
  letter-spacing: -0.5px;
  line-height: 1;
}

.auth-brand__sub {
  font-family: 'DM Mono', monospace;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #6B7280;
  margin-top: 4px;
}

.auth-card {
  width: 100%;
  max-width: 420px;
  border-radius: 12px !important;
  overflow: hidden;
}

.auth-card-header {
  background: #2D6A4F;
  padding: 24px 24px 20px;
  color: white;
}

.auth-card-header h2 {
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 4px;
}

.auth-card-header p {
  font-size: 0.875rem;
  opacity: 0.85;
  margin: 0;
}

.access-section {
  background: #F7F0E6;
  border-radius: 8px;
  padding: 12px 16px;
}

.access-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1A1A2E;
  margin-bottom: 4px;
}

.auth-links {
  margin-top: 20px;
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 0.875rem;
}

.auth-links a {
  color: #2D6A4F;
  font-weight: 600;
  text-decoration: none;
}

.auth-links a:hover {
  text-decoration: underline;
}

.auth-footer {
  margin-top: 12px;
  font-size: 0.8rem;
}

.auth-footer a {
  color: #6B7280;
  text-decoration: none;
}

.auth-footer a:hover {
  color: #2D6A4F;
}
</style>
